import {
  GetQuestionCreationTaskByIdDocument,
  useUpdateQuestionCreationTaskMutation,
} from '@exam-management/graphql';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
  FormDateInput,
  FormModal,
  useTheme,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useQuestionCreationTaskDetails } from '../../providers';

import { type ChangeDueDateModalFormValues } from './schema';

interface ChangeDueDateModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ChangeDueDateFormModal(props: Readonly<ChangeDueDateModalProps>) {
  const { open, onClose } = props;

  const theme = useTheme();
  const { task } = useQuestionCreationTaskDetails();
  const { showSnackbar } = useSnackbar();

  const formContext = useFormContext<ChangeDueDateModalFormValues>();

  const [updateDueDate] = useUpdateQuestionCreationTaskMutation();

  const onSubmit = async (data: ChangeDueDateModalFormValues) => {
    await updateDueDate({
      variables: {
        updateQuestionCreationTaskInput: {
          taskId: task.id,
          name: task.name as string,
          assigneeId: task.assignee?.id as string,
          dueDate: data.dueDate,
        },
      },
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showSnackbar({
          message: 'Task has been saved',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'Failed to save task',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  const handleClose = () => {
    formContext.reset();
    onClose();
  };

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext]);

  return (
    <FormModal open={open} onClose={handleClose} maxWidth='sm'>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>
        <DialogTitle onClose={handleClose}>Edit Due Date</DialogTitle>
        <Divider />
        <DialogContent>
          <FormDateInput
            name='dueDate'
            label='Due Date'
            inputFormat='DD-MM-YYYY'
            minDate={new Date()}
            zIndex={theme.zIndex.modal + 1}
            withinPortal
            required
          />
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={handleClose}>
            Cancel
          </Button>
          <FormButton label='Save' type='submit' />
        </DialogActions>
      </Form>
    </FormModal>
  );
}
